<script setup>
import * as Sentry from '@sentry/vue'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { storeSession } from '../auth.js'

const router = useRouter()

const password = ref('')
const errorMessage = ref('')
const submitting = ref(false)

function logAuthentication(level, outcome, startedAt, attributes = {}) {
  Sentry.logger[level]('Authentication completed', {
    operation: 'auth_login',
    outcome,
    duration_ms: Math.round(performance.now() - startedAt),
    ...attributes,
  })
}

async function onSubmit() {
  const startedAt = performance.now()
  errorMessage.value = ''

  if (!password.value) {
    errorMessage.value = 'Введите пароль.'
    logAuthentication('info', 'rejected', startedAt)
    return
  }

  submitting.value = true
  try {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        password: password.value,
      }),
    })

    if (response.status === 401) {
      errorMessage.value = 'Неверный пароль.'
      logAuthentication('info', 'rejected', startedAt, { http_status: response.status })
      return
    }

    if (response.status === 400) {
      errorMessage.value = 'Введите пароль.'
      logAuthentication('info', 'rejected', startedAt, { http_status: response.status })
      return
    }

    if (!response.ok) {
      errorMessage.value = 'Не удалось войти. Попробуйте еще раз.'
      logAuthentication('warn', 'failed', startedAt, { http_status: response.status })
      return
    }

    const body = await response.json()
    storeSession(body.token, body.expiresAt, body.access)
    logAuthentication('info', 'success', startedAt, { access_level: body.access })
    await router.push({ name: 'home' })
  } catch (error) {
    errorMessage.value = 'Не удалось войти. Попробуйте еще раз.'
    Sentry.captureException(error, {
      tags: { operation: 'auth_login' },
      extra: {
        outcome: 'failed',
        duration_ms: Math.round(performance.now() - startedAt),
      },
    })
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <main class="login-shell">
    <section
      class="login-card"
      aria-labelledby="login-title"
    >
      <h1 id="login-title">
        Вход в Бюджет
      </h1>
      <p class="intro">
        Введите пароль администратора или пароль только для чтения.
      </p>

      <form
        class="login-form"
        novalidate
        @submit.prevent="onSubmit"
      >
        <label class="field">
          <span>Пароль</span>
          <input
            v-model="password"
            type="password"
            autocomplete="current-password"
            autofocus
            required
          >
        </label>

        <p
          v-if="errorMessage"
          class="error"
          role="alert"
        >
          {{ errorMessage }}
        </p>

        <button
          type="submit"
          class="primary"
          :disabled="submitting"
        >
          {{ submitting ? 'Входим…' : 'Войти' }}
        </button>
      </form>
    </section>
  </main>
</template>

<style scoped>
.login-shell {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: var(--space-xl);
}

.login-card {
  width: min(440px, 100%);
  padding: clamp(2rem, 4vw, 3rem);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-xl);
  background: var(--surface-raised);
  box-shadow: var(--elevation-raised);
}

.login-card h1 {
  margin: 0 0 var(--space-sm);
  color: var(--text-primary);
  font-size: clamp(2rem, 4vw, 2.5rem);
  letter-spacing: -0.04em;
  text-wrap: balance;
}

.login-card .intro {
  margin-bottom: var(--space-xl);
  color: var(--text-secondary);
  text-wrap: pretty;
}

.login-form {
  display: grid;
  gap: var(--space-lg);
}

.field {
  display: grid;
  gap: 0.4rem;
  font-size: 0.95rem;
  color: var(--text-secondary);
  font-weight: 600;
}

.field input {
  padding: 0.75rem 1rem;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-overlay);
  color: var(--text-primary);
  font-size: 1rem;
  touch-action: manipulation;
  transition: border-color var(--duration-fast) var(--ease-out);
}

.field input:focus {
  outline: 2px solid var(--border-focus);
  outline-offset: 2px;
}

.error {
  margin: 0;
  padding: 0.75rem 1rem;
  border-radius: var(--radius-lg);
  background: var(--error-bg);
  color: var(--error-text);
  font-size: 0.95rem;
}

.primary {
  padding: 0.85rem 1rem;
  border: none;
  border-radius: var(--radius-xl);
  background: var(--accent-bg);
  color: var(--accent-text);
  font-weight: 600;
  cursor: pointer;
  touch-action: manipulation;
  transition: background var(--duration-fast) var(--ease-out);
}

.primary:hover:not(:disabled) {
  background: var(--accent-bg-hover);
}

.primary:disabled {
  background: var(--surface-sunken);
  color: var(--text-tertiary);
  cursor: progress;
}

@media (max-width: 760px) {
  .login-shell {
    align-items: stretch;
    min-height: 100svh;
    padding: var(--space-md);
  }

  .login-card {
    width: min(440px, calc(100% - 2rem));
    margin: auto;
    padding: var(--space-lg);
    border-radius: var(--radius-lg);
  }

  .login-card h1 {
    font-size: 2rem;
  }

  .login-card .intro {
    margin-bottom: var(--space-lg);
  }

  .field input,
  .primary {
    min-height: var(--touch-target-min);
  }
}
</style>
